/**
 * Los tokens que el generador sabe rellenar.
 *
 * Se declaran aquí en vez de importarlos de Xurvey: son dos repositorios
 * distintos y este motor no puede depender del árbol del otro. Es un subconjunto
 * a propósito — de todo el tema, la paleta sólo produce colores— y el runtime
 * que los consume sigue siendo el de Xurvey, que ignora lo que no reconozca.
 */
export interface ThemeTokens {
  bg: string;
  surface: string;
  primary: string;
  onPrimary: string;
  accent: string;
  text: string;
  muted: string;
  border: string;
  danger: string;
}
import { contrast, mix, mk, relLum } from './color';
import type { Color, RenderResult } from './types';

/**
 * De la paleta del fondo a los colores de la encuesta.
 *
 * El generador ya decide los cinco colores con reglas duras —reparto 60/30/10,
 * armonía de matiz, neutros teñidos, ajuste a gamut—. Lo que faltaba era
 * llevarlos a los tokens que la encuesta pinta de verdad, que es donde el
 * trabajo se nota: sin este paso, quien diseñaba un fondo precioso se
 * encontraba encima los colores del tema anterior.
 *
 * **El reparto 60/30/10 no es decorativo, dice qué va dónde.** El 60 % es la
 * superficie más grande y ésa es el fondo. El 30 % es lo segundo más grande:
 * las tarjetas. El 10 % es lo que hay que mirar: el botón de continuar. Mapear
 * al revés —el acento de fondo— da una pantalla que grita y un botón que
 * desaparece, que es exactamente el error que estas reglas evitan.
 *
 * **Lo que NO se toca es el rojo de error.** Un «error» derivado de una paleta
 * verde sale verde, y entonces deja de significar error: ese color no es
 * estético, es semántico, y lo lee alguien que ya está teniendo un problema.
 */

/** Un color de la paleta, con lo que necesita el resto del módulo. */
type C = Color;

export interface PaletaAplicada {
  tokens: Partial<ThemeTokens>;
  /** Contraste real del texto sobre el fondo, para poder enseñarlo. */
  ratioTexto: number;
  /** Contraste del texto del botón sobre el botón. */
  ratioBoton: number;
  /** Contraste del botón contra el fondo: es lo que decide si se ve. */
  ratioBotonFondo: number;
  /** El color final del botón, que puede no ser el acento crudo. */
  boton: string;
}

export function tokensDesdeLaPaleta(result: RenderResult): PaletaAplicada {
  const { palette: P, textColor } = result;

  /*
   * La tinta la decide el fondo REAL, no el color dominante.
   *
   * `textColor` lo calcula el render midiendo la luminancia media de la imagen
   * ya pintada —con su trama, su viñeta y su grano encima—, que puede quedar
   * bastante más clara o más oscura que el dominante a secas. Elegir aquí por
   * `P.dom` volvería a adivinar lo que allí ya se midió.
   */
  const texto = textColor;

  /*
   * El botón tiene que verse. El acento tal cual no siempre se ve.
   *
   * El acento del generador está elegido para funcionar DENTRO de la imagen —es
   * el 10 % que remata una composición—, y sus políticas pueden dejarlo más
   * oscuro que el propio fondo: la de vibración, sin ir más lejos, separa la
   * luminosidad hacia donde haga falta. Medido en el primer fondo que probé:
   * dominante #005087 y acento #003848, o sea un botón de continuar más oscuro
   * que la pantalla sobre la que se apoya.
   *
   * Un color de relleno de una composición y un control que hay que pulsar no
   * son el mismo problema. La WCAG lo separa igual: 3:1 para componentes de
   * interfaz. Así que se conserva el matiz y el croma del acento —su identidad—
   * y se le mueve la luminosidad EN CONTRA del fondo hasta que llegue a 3:1.
   * Cambiar el color por un neutro cumpliría el contraste y perdería la marca.
   */
  const boton = eligeBoton(P.acc, P.dom, P.light, P.dark);

  /*
   * El texto del botón: el neutro que más contraste dé contra el botón.
   *
   * Se prueban los dos en vez de asumir «claro sobre color»: un acento amarillo
   * o lima —que los hay, y son los que más gustan— pide tinta oscura, y con la
   * regla fija saldría blanco sobre amarillo, que es el clásico ilegible.
   */
  /*
   * Se estiran LOS DOS y gana el que acabe más lejos del botón.
   *
   * Los neutros del generador son fijos —0.965 y 0.185— y están pensados como
   * tinta sobre el FONDO, no sobre el botón, así que con el fondo muy claro se
   * quedaban en 4.24–4.50 contra él: rozando el límite, que en la práctica es
   * fallarlo. Se les empuja la luminosidad conservando su tinte, que es lo que
   * evita que un neutro se vea sucio junto al color (política P-05).
   *
   * Elegir primero y estirar después no vale, y costó un caso del barrido: con
   * el botón en #767183 el oscuro sale ganador por poco y topa en 4.41 —ningún
   * negro da más contra ese gris—, mientras que el claro llega a 4.76. La
   * comparación hay que hacerla sobre el resultado final, no sobre el de
   * partida.
   */
  const sobreBoton = mejorContraste(
    separaHastaContraste(P.light, boton, 4.5),
    separaHastaContraste(P.dark, boton, 4.5),
    boton,
  );

  /*
   * El texto suave se acerca al fondo hasta donde el contraste aguanta.
   *
   * Un 60 % hacia el fondo es lo que se ve «secundario» sin desaparecer, pero
   * se comprueba: si ese punto baja de 4.5:1 se retrocede por pasos. Fijar un
   * porcentaje y confiar es cómo un texto de ayuda acaba siendo invisible en
   * los fondos claros y perfectamente legible en los oscuros, sin que nadie lo
   * note hasta que un cliente lo dice.
   */
  const suave = acercaSinPerderContraste(texto, P.dom, 0.6, 4.5);

  /*
   * El borde es la línea más callada de la pantalla: separa sin pintar. Se saca
   * de la superficie hacia la tinta, no del fondo, porque lo que enmarca son
   * las tarjetas.
   */
  const borde = mix(P.sec, texto, 0.22);

  return {
    tokens: {
      bg: P.dom.hex,
      surface: P.sec.hex,
      primary: boton.hex,
      onPrimary: sobreBoton.hex,
      accent: boton.hex,
      text: texto.hex,
      muted: suave.hex,
      border: borde.hex,
      // `danger` a propósito fuera: ver la cabecera.
    },
    ratioTexto: result.ratio,
    ratioBoton: contrast(sobreBoton.rgb, boton.rgb),
    ratioBotonFondo: contrast(boton.rgb, P.dom.rgb),
    boton: boton.hex,
  };
}

/**
 * Aleja la luminosidad de `c` respecto a `fondo` hasta llegar al contraste
 * pedido, conservando matiz y croma.
 *
 * **Se prueban las dos direcciones y gana la que menos mueva el color.** La
 * primera versión elegía el sentido según si el fondo era oscuro o claro, y un
 * barrido del círculo de matiz la tumbó: con el fondo a L=0.70 y el acento ya
 * en 0.95, «el fondo es oscuro, sube» empujaba un color casi blanco contra el
 * tope y lo dejaba en 2.5:1 —peor que no tocarlo—, cuando bajar lo resolvía de
 * sobra. Que el fallo sólo apareciera en cinco de sesenta combinaciones es
 * justamente por lo que la regla se comprueba barriendo y no con un ejemplo.
 *
 * Los topes son 0.06 y 0.97: ni negro ni blanco puros, que es la misma política
 * P-01 del generador —no admiten sombra ni brillo y el botón se ve como un
 * agujero recortado—.
 */
function separaHastaContraste(c: C, fondo: C, minimo: number): C {
  if (contrast(c.rgb, fondo.rgb) >= minimo) return c;

  const candidatos: C[] = [];
  for (const paso of [0.03, -0.03]) {
    let L = c.L;
    for (let i = 0; i < 32; i++) {
      L += paso;
      if (L > 0.97 || L < 0.06) break;
      const probado = mk(L, c.C, c.h, c.role);
      if (contrast(probado.rgb, fondo.rgb) >= minimo) {
        candidatos.push(probado);
        break;
      }
    }
  }
  if (candidatos.length > 0) {
    // El que menos se aleje del acento original: se corrige lo justo para que
    // el botón se vea, no se reinventa el color.
    return candidatos.sort((a, b) => Math.abs(a.L - c.L) - Math.abs(b.L - c.L))[0]!;
  }

  /*
   * Ninguna dirección llega. Se devuelve el extremo que MÁS contraste dé, que
   * sigue siendo mejor que el original, y no se cambia a un neutro a la fuerza:
   * si un matiz muy saturado no separa de su propio fondo, el problema es el
   * fondo, y quien diseña lo ve en la previsualización y en la métrica.
   */
  const extremos = [mk(0.97, c.C, c.h, c.role), mk(0.06, c.C, c.h, c.role)];
  return contrast(extremos[0]!.rgb, fondo.rgb) >= contrast(extremos[1]!.rgb, fondo.rgb)
    ? extremos[0]!
    : extremos[1]!;
}

/** De dos candidatos, el que más contraste dé contra `sobre`. */
function mejorContraste(a: C, b: C, sobre: C): C {
  return contrast(a.rgb, sobre.rgb) >= contrast(b.rgb, sobre.rgb) ? a : b;
}

/**
 * El color del botón: las dos condiciones a la vez, no una detrás de otra.
 *
 * Un botón tiene que **separarse del fondo** (3:1, lo que la WCAG pide a un
 * componente de interfaz) y además **admitir su propio texto** (4.5:1). Las dos
 * tiran en direcciones distintas: separarse del fondo empuja el color hacia el
 * extremo contrario, y admitir texto lo empuja hacia un extremo cualquiera —lo
 * que no vale es quedarse a media altura, que es donde ni el blanco ni el negro
 * llegan a 4.5—.
 *
 * Resolverlas en dos pasos —primero el fondo, luego estirar el texto— parece
 * natural y no funciona: el barrido dio un botón en #767183, a media
 * luminosidad, donde el mejor texto posible se queda en 4.45 haga lo que haga.
 * Ninguna corrección posterior del TEXTO arregla eso; hay que mover el BOTÓN.
 *
 * Así que se recorre la luminosidad entera conservando matiz y croma, y se
 * elige el punto que cumple las dos y menos se aleja del acento original. Si
 * ninguno cumple —matices muy saturados contra su propio fondo—, gana el que
 * mejor separa del fondo: la métrica del diálogo lo enseña y quien diseña
 * decide.
 */
function eligeBoton(acento: C, fondo: C, claro: C, oscuro: C): C {
  const admiteTexto = (c: C) =>
    Math.max(contrast(claro.rgb, c.rgb), contrast(oscuro.rgb, c.rgb)) >= 4.5;

  const candidatos: C[] = [];
  for (let L = 0.06; L <= 0.97; L += 0.02) {
    const c = mk(L, acento.C, acento.h, acento.role);
    if (contrast(c.rgb, fondo.rgb) >= 3 && admiteTexto(c)) candidatos.push(c);
  }
  if (candidatos.length > 0) {
    return candidatos.sort((a, b) => Math.abs(a.L - acento.L) - Math.abs(b.L - acento.L))[0]!;
  }
  return separaHastaContraste(acento, fondo, 3);
}

/**
 * Acerca `desde` hacia `hacia` todo lo que se pueda sin bajar del contraste
 * pedido contra `hacia`. Devuelve el punto más cercano que aún cumple.
 */
function acercaSinPerderContraste(desde: C, hacia: C, t: number, minimo: number): C {
  let paso = t;
  for (let i = 0; i < 8; i++) {
    const c = mix(desde, hacia, paso);
    if (contrast(c.rgb, hacia.rgb) >= minimo) return c;
    paso -= t / 8;
    if (paso <= 0) break;
  }
  return desde;
}

/**
 * Lo que enseña la barra de muestras, en el orden del reparto.
 *
 * La casilla del acento enseña el color que se va a APLICAR, no el que salió
 * del generador. Si la barra dijera `#003848` y el botón acabara siendo otro,
 * la muestra estaría mintiendo sobre el único sitio donde se va a ver ese
 * color — y una previsualización que miente es peor que no tenerla. Cuando hay
 * ajuste se marca, para que quien mira sepa que pasó algo y por qué.
 */
export function muestrasDeLaPaleta(result: RenderResult): Array<{
  rol: string;
  peso: string;
  hex: string;
  /** Tinta legible ENCIMA de esta muestra, para el rótulo. */
  tinta: string;
  /** El motor lo movió para que el botón se vea contra el fondo. */
  ajustado?: boolean;
}> {
  const { palette: P } = result;
  const aplicada = tokensDesdeLaPaleta(result);
  const acento = aplicada.boton;

  const filas: Array<[string, string, string]> = [
    ['Dominante', '60%', P.dom.hex],
    ['Secundario', '30%', P.sec.hex],
    ['Acento', '10%', acento],
    ['Claro', 'texto', P.light.hex],
    ['Oscuro', 'sombra', P.dark.hex],
  ];
  return filas.map(([rol, peso, hexa]) => ({
    rol,
    peso,
    hex: hexa,
    // El rótulo de cada muestra se pinta ENCIMA de ella, así que su color
    // depende de la muestra y no del tema de la herramienta.
    tinta: relLum(aHexRgb(hexa)) > 0.4 ? '#111111' : '#FFFFFF',
    ...(rol === 'Acento' && acento !== P.acc.hex ? { ajustado: true } : {}),
  }));
}

const aHexRgb = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
